import { useQuery } from "@tanstack/react-query";
import Modal from "react-responsive-modal";

import style from "./style.module.scss";
import { mediaByProductQueryOptions, type Media } from "../../../data/queries/media";
import { useUploadWithProgress, useBodyScrollLock, type UploadResult } from "../../../hooks";
import ImageDropZone from "../image-drop-zone";
import { FileButton, Spinner } from "../../../ui";

interface PhotoItemProps {
  image: Media;
  onSelect: (url: string) => void;
}

function PhotoItem({ image, onSelect }: PhotoItemProps) {
  return (
    <div className={style.photoItem} onClick={() => onSelect(image.url)}>
      <img src={image.url} alt="" className={style.photoImage} />
    </div>
  );
}

interface PhotoLibraryPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  productId: string;
  userId: string;
  onPreviewChange?: (url: string | undefined) => void;
}

function PhotoLibraryPicker({
  open,
  onClose,
  onSelect,
  productId,
  userId,
  onPreviewChange,
}: PhotoLibraryPickerProps) {
  const { data: media = [], isLoading } = useQuery(mediaByProductQueryOptions(productId, userId));

  const { uploads, uploadFiles, isUploading } = useUploadWithProgress({
    productId,
    userId,
  });

  useBodyScrollLock(open);

  const handleSelectFiles = async (files: File[]) => {
    // Show preview immediately if callback provided
    if (onPreviewChange && files[0]) {
      const previewUrl = URL.createObjectURL(files[0]);
      onPreviewChange(previewUrl);
    }

    const results = await uploadFiles(files);

    // Clear preview and select first successful upload
    if (onPreviewChange) {
      onPreviewChange(undefined);
    }

    const firstSuccess = results.find(
      (r): r is PromiseFulfilledResult<UploadResult> =>
        r.status === "fulfilled" && r.value?.success === true,
    );
    if (firstSuccess?.value?.success && firstSuccess.value.media?.url) {
      onSelect(firstSuccess.value.media.url);
      onClose();
    }
  };

  const handlePhotoSelect = (url: string) => {
    onSelect(url);
    onClose();
  };

  const pendingUploads = Object.values(uploads).filter(
    (u) => u.status === "pending" || u.status === "uploading",
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      blockScroll={false}
      classNames={{
        modal: style.modal,
      }}
    >
      <h3 className={style.title}>Choisir une photo</h3>

      <div className={style.content}>
        {isLoading && <Spinner />}

        {!isLoading && (
          <div className={style.photoGrid}>
            {/* Existing photos */}
            {media.map((image) => (
              <PhotoItem key={image.uid} image={image} onSelect={handlePhotoSelect} />
            ))}

            {/* Upload button last */}
            <FileButton
              accept="image/*"
              multiple={false}
              onSelect={handleSelectFiles}
              render={({ onClick }) => (
                <div className={style.uploadButton} onClick={onClick}>
                  <ImageDropZone
                    loading={isUploading}
                    progress={pendingUploads[0]?.progress || 0}
                    className={style.uploadButtonInner}
                    onDrop={handleSelectFiles}
                  />
                </div>
              )}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default PhotoLibraryPicker;
